import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Autocomplete,
} from "@mui/material";
import { useEnglishText } from "../../../text/useEnglishText.js";
import { manageCompany } from "../../../services/companyService";
import { getLookupData } from "../../../services/lookupService";
import { useSnackbar } from "../../../components//common/AppSnackbar";

function CompanyManageModal({ open, company, onClose, onSuccess }) {
  const { t } = useEnglishText();
  const { showSuccess, showError } = useSnackbar();
  const isEditMode = !!company;

  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    company_name: "",
    company_abbriviation: "",
    lt_country_id: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // ۱. دریافت لیست کشورها در زمان اولین Mount شدن
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const res = await getLookupData("COUNTRY");
        setCountries(
          res.map((c) => ({
            id: String(c.LT_COUNTRY_ID),
            label: c.COUNTRY_NAME,
          })),
        );
      } catch (e) {
        console.error("Error loading countries:", e);
      }
    };
    loadCountries();
  }, []);

  // ۲. مدیریت مقادیر فرم هنگام باز شدن مدال یا تغییر آیتم انتخاب شده
  useEffect(() => {
    if (open) {
      if (company) {
        setFormData({
          company_name: company.COMPANY_NAME || "",
          company_abbriviation: company.COMPANY_ABBRIVIATION || "",
          lt_country_id: company.LT_COUNTRY_ID
            ? String(company.LT_COUNTRY_ID)
            : "",
        });
      } else {
        setFormData({
          company_name: "",
          company_abbriviation: "",
          lt_country_id: "",
        });
      }
    }
  }, [company, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ۱. عملیات ثبت و ویرایش
  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const action = isEditMode ? "UPDATE" : "ADD";
      const res = await manageCompany({
        action,
        company_id: company?.COMPANY_ID || "",
        ...formData,
      });

      // تبدیل مقدار ERROR_NUMBER به عدد یا مقایسه رشته‌ای دقیق
      if (res && String(res[0].ERROR_NUMBER) === "0") {
        showSuccess(res[0].ERROR_MESSAGE || t("common.success"));
        onSuccess();
        onClose();
      } else {
        // نمایش مستقیم پیام خطای ارسالی از سرور
        showError(res[0]?.ERROR_MESSAGE || t("common.error_occurred"));
      }
    } catch (err) {
      console.error(err);
      showError(t("common.error_occurred"));
    } finally {
      setSubmitting(false);
    }
  };

  // ۲. عملیات حذف کمپانی
  const handleDelete = async () => {
    if (
      !window.confirm(
        t(
          "companies.confirm_delete",
          "Are you sure you want to delete this company?",
        ),
      )
    ) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await manageCompany({
        action: "DELETE",
        company_id: company.COMPANY_ID,
      });

      if (res && String(res[0]?.ERROR_NUMBER) === "0") {
        showSuccess(res[0]?.ERROR_MESSAGE || t("common.success"));
        onSuccess();
        onClose();
      } else {
        showError(res[0]?.ERROR_MESSAGE || t("common.error_occurred"));
      }
    } catch (err) {
      console.error(err);
      showError(t("common.error_occurred"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { direction: "ltr" },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {isEditMode
          ? t("companies.edit_company", "Edit Company")
          : t("companies.add_company", "Add Company")}
      </DialogTitle>

      <form onSubmit={handleSave}>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              label={t("companies.name", "Company Name")}
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
            <TextField
              label={t("companies.abbreviation", "Abbreviation")}
              name="company_abbriviation"
              value={formData.company_abbriviation}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />

            <Box>
              <Autocomplete
                options={countries}
                value={
                  countries.find(
                    (c) => String(c.id) === String(formData.lt_country_id),
                  ) || null
                }
                onChange={(e, v) => {
                  setFormData((prev) => ({
                    ...prev,
                    lt_country_id: v?.id || "",
                  }));
                }}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("profile.country", "Country")}
                    required
                    fullWidth
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Box>
            {isEditMode && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleDelete}
                disabled={submitting}
              >
                {t("common.delete", "Delete")}
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onClose} disabled={submitting}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
            >
              {submitting
                ? t("common.saving", "Saving...")
                : t("common.save", "Save")}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CompanyManageModal;
